import { GripVertical, Eye, EyeOff, X } from 'lucide-react';

/**
 * The inner drag-and-toggle row list. No chrome, no positioning —
 * caller owns the surrounding card / popover / inline container.
 *
 * Used directly by TelemetryCardPicker (which provides its own
 * dark-glass card) and indirectly by ColumnManager (which wraps it
 * in the original light-glass absolute popover for VehicleListPanel).
 *
 * Props mirror ColumnManager's, minus `onClose` (chrome-only).
 */
export function ColumnList({ columns, visible, setVisible, dragIdx, setDragIdx, setColOrder }) {
  const toggle = (id) => {
    const col = columns.find((c) => c.id === id);
    if (col?.locked) return;
    setVisible((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  return (
    <div className="py-1 max-h-72 overflow-y-auto">
      {columns.map((col, i) => {
        const isVisible = visible.includes(col.id);
        return (
          <div
            key={col.id}
            draggable={!col.locked}
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
            onDrop={() => {
              if (dragIdx == null || dragIdx === i) return;
              const ids = columns.map((c) => c.id);
              const [moved] = ids.splice(dragIdx, 1);
              ids.splice(i, 0, moved);
              setColOrder(ids);
              setDragIdx(null);
            }}
            onDragEnd={() => setDragIdx(null)}
            className={`flex items-center gap-1.5 px-2 py-[5px] mx-1 rounded-lg transition-colors ${dragIdx === i ? 'bg-blue-500/10' : 'hover:bg-white/40'} ${col.locked ? 'opacity-50' : ''}`}
          >
            <span className={`${col.locked ? 'invisible' : 'cursor-grab active:cursor-grabbing'} text-slate-300`}>
              <GripVertical size={12} />
            </span>
            <button
              type="button"
              onClick={() => toggle(col.id)}
              disabled={col.locked}
              className={`transition-colors ${isVisible ? 'text-emerald-500' : 'text-slate-300'}`}
            >
              {isVisible ? <Eye size={12} /> : <EyeOff size={12} />}
            </button>
            <span className={`text-[11px] flex-1 ${isVisible ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>
              {col.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Column visibility + reorder popover.
 *
 * Used by VehicleListPanel (vehicles + people column managers).
 * Renders as an absolute-positioned light-glass card anchored to the
 * caller's relative parent. For inline / nested embedding (e.g.
 * inside another popover), import { ColumnList } instead and render
 * it without this chrome.
 *
 * Props:
 *  - columns:     [{ id, label, locked? }]  full list (locked items can't be hidden)
 *  - visible:     string[]                  list of column ids that are currently visible
 *  - setVisible:  (prev => next) => void    functional updater
 *  - onClose:     () => void
 *  - t:           translation function
 *  - dragIdx:     number | null             current drag state (caller-owned)
 *  - setDragIdx:  (number | null) => void
 *  - setColOrder: (string[]) => void        commits a new order
 */
export default function ColumnManager({ columns, visible, setVisible, onClose, t, dragIdx, setDragIdx, setColOrder }) {
  return (
    <div
      className="absolute end-0 top-full mt-1.5 z-50 w-56 rounded-2xl overflow-hidden shadow-lg shadow-black/[0.05]"
      style={{
        background: 'var(--glass-bg)',
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        border: '1px solid var(--glass-border)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-2.5 flex items-center justify-between" style={{ borderBottom: '1px solid var(--glass-border)' }}>
        <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300">{t('vehicleList.columns')}</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={13} /></button>
      </div>
      <ColumnList
        columns={columns}
        visible={visible}
        setVisible={setVisible}
        dragIdx={dragIdx}
        setDragIdx={setDragIdx}
        setColOrder={setColOrder}
      />
    </div>
  );
}
